import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';

// =====================================================
// CONTEXT
// =====================================================

export const DashboardContext = createContext(null);

// =====================================================
// PROVIDER
// =====================================================

export function DashboardProvider({ children }) {

  // ---------------------------------------------------
  // Selected Dashboard Date
  // ---------------------------------------------------

  const [selectedDate, setSelectedDate] = useState(
    () => {
      const date = new Date();
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }
  );

  // ---------------------------------------------------
  // Dashboard Data
  // ---------------------------------------------------

  const [dashboardData, setDashboardData] = useState({
    header: null,
    quickReports: null,
    todaysChecklist: null,
    rotaToday: null,
    temperatureOverview: null,
    complianceCentre: null,
    recentIncidents: null,
    upcomingExpiring: null,
  });

  // ---------------------------------------------------
  // Loading State
  // ---------------------------------------------------

  const [loading, setLoading] = useState({
    header: false,
    quickReports: false,
    todaysChecklist: false,
    rotaToday: false,
    temperatureOverview: false,
    complianceCentre: false,
    recentIncidents: false,
    upcomingExpiring: false,
  });

  // ---------------------------------------------------
  // Error State
  // ---------------------------------------------------

  const [errors, setErrors] = useState({});

  // ---------------------------------------------------
  // Refresh Dashboard
  // ---------------------------------------------------

  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const sections = ['header', 'rotaToday', 'temperatureOverview', 'complianceCentre', 'recentIncidents', 'upcomingExpiring'];
    setDashboardData(previous => ({ ...previous, ...Object.fromEntries(sections.map(key => [key, null])) }));
    setLoading(previous => ({ ...previous, ...Object.fromEntries(sections.map(key => [key, true])) }));
    setErrors({});
    async function load() {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/dashboard?date=${encodeURIComponent(selectedDate)}`, {
          signal: controller.signal,
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.message || 'Unable to load dashboard');
        if (!controller.signal.aborted) setDashboardData(previous => ({ ...previous, ...result.data }));
      } catch (error) {
        if (!controller.signal.aborted) setErrors(Object.fromEntries(sections.map(key => [key, error.message])));
      } finally {
        if (!controller.signal.aborted) setLoading(previous => ({ ...previous, ...Object.fromEntries(sections.map(key => [key, false])) }));
      }
    }
    load();
    return () => controller.abort();
  }, [selectedDate, refreshKey]);

  const refreshDashboard = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  // ---------------------------------------------------
  // Update Individual Dashboard Data
  // ---------------------------------------------------

  const updateDashboardData = useCallback((section, data) => {
    setDashboardData((prev) => ({
      ...prev,
      [section]: data,
    }));
  }, []);

  // ---------------------------------------------------
  // Update Individual Loading State
  // ---------------------------------------------------

  const updateLoading = useCallback((section, value) => {
    setLoading((prev) => ({
      ...prev,
      [section]: value,
    }));
  }, []);

  // ---------------------------------------------------
  // Update Individual Error
  // ---------------------------------------------------

  const updateError = useCallback((section, error) => {
    setErrors((prev) => ({
      ...prev,
      [section]: error,
    }));
  }, []);

  // ---------------------------------------------------
  // Clear Errors
  // ---------------------------------------------------

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  // ---------------------------------------------------
  // Reset Dashboard Data
  // ---------------------------------------------------

  const resetDashboard = useCallback(() => {
    setDashboardData({
      header: null,
      quickReports: null,
      todaysChecklist: null,
      rotaToday: null,
      temperatureOverview: null,
      complianceCentre: null,
      recentIncidents: null,
      upcomingExpiring: null,
    });

    setErrors({});
  }, []);

  // ---------------------------------------------------
  // CONTEXT VALUE
  // ---------------------------------------------------

  const value = {
    selectedDate,
    setSelectedDate,

    dashboardData,
    setDashboardData,
    updateDashboardData,

    loading,
    setLoading,
    updateLoading,

    errors,
    setErrors,
    updateError,
    clearErrors,

    refreshKey,
    refreshDashboard,

    resetDashboard,
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
}

// =====================================================
// CUSTOM HOOK
// =====================================================

export function useDashboard() {
  const context = useContext(DashboardContext);

  if (!context) {
    throw new Error(
      'useDashboard must be used inside DashboardProvider'
    );
  }

  return context;
}

export default DashboardContext;
